import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Textarea,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { FaTrophy } from "react-icons/fa";
import PageHeader from "../components/PageHeader";
import { getAllPools } from "../api/pools";
import { getActiveSeason, getAllMlbPools } from "../api/mlbHrDerby";
import { getPsActiveSeason, getAllPsPools } from "../api/mlbPlayoff";
import { getAllWcPools } from "../api/worldCup";
import { getAllSurvivorPools, getNflCurrentSeason } from "../api/nflSurvivor";
import { getAllPickemPools } from "../api/nflPickem";
import { submitFeedback } from "../api/feedback";
import { getRecordGames, getRecords } from "../api/records";
import { usePlSeason } from "../hooks/usePlSeason";

import plLogo from "../assets/logos/premier-league.png";
import mlbLogo from "../assets/logos/mlb.png";
import mlbPlayoffLogo from "../assets/logos/mlb-playoffs.png";
import worldCupLogo from "../assets/logos/worldcup.png";
import nflLogo from "../assets/logos/nfl.png";
import wwelogo from "../assets/logos/wwe.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const DEEP_END_RED = "#8B0000";
const DIVIDER_GRAY = "#7a7a7a";

const games = [
  {
    title: "Premier League Pick 'Em",
    subtitle: "Make weekly picks and stay atop the leaderboard.",
    route: "/premier-league",
    active: true,
    logo: plLogo,
    details: {
      heading: "Premier League Pick 'Em",
      intro:
        "Pick a team each matchweek and compete against the rest of the pool across the full Premier League season.",
      bullets: [
        "Make one pick and select confidence points each matchweek from the available fixtures.",
        "Team can only be picked once per half of the season.",
        "Limit on picks against the same opponent.",
        "Picks lock based on kickoff timing.",
        "Standings, scoring, and matchweek summaries update as results are finalized.",
        "Autopick rules apply if a player misses a matchweek pick.",
      ],
    },
  },
  {
    title: "MLB Home Run Derby",
    subtitle: "Draft six sluggers and track their home runs all season long.",
    route: "/mlb-hr-derby",
    active: true,
    logo: mlbLogo,
    details: {
      heading: "MLB Home Run Derby",
      intro:
        "Pick the six players you believe will hit the most home runs — in total — during the regular season.",
      bullets: [
        "Select SIX players whose combined home run total from the previous Major League regular season does not exceed 130.",
        "During the season you are allowed ONE AND ONLY ONE roster change, which may be made at any time and for any reason.",
        "To make a valid change, the replacement player must not exceed the replaced player's home run total in either (a) the previous season or (b) the current season at the time of the change.",
      ],
    },
  },
  {
    title: "MLB Playoff Challenge",
    subtitle: "Pick six playoff hitters, get the most total bases, and win!",
    route: "/mlb-playoff",
    active: true,
    logo: mlbPlayoffLogo,
    details: {
      heading: "MLB Playoff Challenge",
      intro: "Pick the six players you believe will get the most total bases during the playoffs.",
      bullets: [
        "Most total bases for the entire playoff wins.",
        "Once a team plays their first game, those players are unavailable for the rest of the playoffs.",
        "Point Structure:",
        { text: "Single: 1 point", indent: true },
        { text: "Double: 2 points", indent: true },
        { text: "Triple: 3 points", indent: true },
        { text: "Home Run: 4 points", indent: true },
        { text: "Walk/Out/Error/HBP: 0 points", indent: true },
      ],
    },
  },
  {
    title: "NFL Pick 'Em",
    subtitle: "Make weekly picks and stay atop the leaderboard.",
    route: "/nfl-pickem",
    active: true,
    logo: nflLogo,
    details: {
      heading: "NFL Pick 'Em",
      intro:
        "Pick one team each week and compete against the rest of the pool across the NFL season.",
      bullets: [
        "Make one pick and select confidence points each matchweek from the available games.",
        "Team can only be picked once during the season.",
        "Limit on picks against the same opponent.",
        "Picks lock based on kickoff timing.",
        "Autopick rules apply if a player misses a pick.",
        "Other scoring bonuses available."
      ],
    },
  },
  {
    title: "NFL Survivor",
    subtitle: "Pick a winner or get eliminated.",
    route: "/nfl-survivor",
    active: true,
    logo: nflLogo,
    details: {
      heading: "NFL Survivor",
      intro:
        "Pick one team each week that you think will win.",
      bullets: [
        "Pick one winner for the week.",
        "Team can only be picked once during the season.",
        "If the team wins, you stay in.",
        "If the team loses or ties, you are out.",
        "Picks lock based on kickoff timing.",
        "Autopick rules apply if a player misses a pick."
      ],
    },
  },
  {
    title: "World Cup Pool",
    subtitle: "Draft countries and finish with the most total points for the win!",
    route: "/world-cup",
    active: true,
    logo: worldCupLogo,
    logoStyle: { maxHeight: "90px", maxWidth: "180px" },
    details: {
      heading: "World Cup Pool",
      intro: "Pick the countries you think will perform the best in the upcoming World Cup.",
      bullets: [
        "The draft order will be determined by a random draw.",
        "The draft will snake each round.",
        "Points will be awarded based on wins, draws, and goals.",
        "Bonus points are awarded for advancing to different stages of the tournament.",
        "One swap opportunity is available after the group stage and prior to the knockout stages (optional).",
      ],
    },
  },
  {
    title: "Royal Rumble Randomizer",
    subtitle: "Get one or more wrestlers and hope they are the last standing.",
    route: "#",
    active: false,
    logo: wwelogo,
    details: {
      heading: "Royal Rumble",
      intro: "Get assigned random number(s) 1-30. When that number wrestler enters the ring, cheer them on until they are tossed out or win.",
      bullets: [
        "All participants are put in a randomizer and assigned numbers 1-30.",
        "If there are not 30 participants; multiple numbers per participant.",
        "The number correlates with the order the wrestlers come out.",
        "Last man/woman standing wins."
      ],
    },
  },
];

export default function GameHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { seasonId: PL_SEASON_ID } = usePlSeason();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isIdeaOpen, onOpen: onIdeaOpen, onClose: onIdeaClose } = useDisclosure();
  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackClose } = useDisclosure();
  const { isOpen: isContactOpen, onOpen: onContactOpen, onClose: onContactClose } = useDisclosure();

  const [contactMessage, setContactMessage] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState("");

  function handleContactClose() {
    onContactClose();
    setContactMessage("");
    setContactSubmitted(false);
    setContactError("");
  }

  async function handleContactSubmit() {
    setContactSubmitting(true);
    setContactError("");
    try {
      await submitFeedback({ type: "provide_feedback", game: "all", message: contactMessage });
      setContactSubmitted(true);
    } catch {
      setContactError("Something went wrong. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  }

  const emptyFeedback = { type: "", game: "", message: "" };
  const [feedbackForm, setFeedbackForm] = useState(emptyFeedback);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  function handleFeedbackClose() {
    onFeedbackClose();
    setFeedbackForm(emptyFeedback);
    setFeedbackSubmitted(false);
    setFeedbackError("");
  }

  async function handleFeedbackSubmit() {
    setFeedbackSubmitting(true);
    setFeedbackError("");
    try {
      await submitFeedback(feedbackForm);
      setFeedbackSubmitted(true);
    } catch {
      setFeedbackError("Something went wrong. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  }
  const [selectedGame, setSelectedGame] = useState(null);

  const emptyIdea = { name: "", email: "", sport: "", dataNeeded: "", currentUsers: "", yearsRunning: "", description: "" };
  const [ideaForm, setIdeaForm] = useState(emptyIdea);
  const [ideaSubmitted, setIdeaSubmitted] = useState(false);
  const [ideaSubmitting, setIdeaSubmitting] = useState(false);
  const [ideaError, setIdeaError] = useState("");

  function handleIdeaChange(field, value) {
    setIdeaForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleIdeaClose() {
    onIdeaClose();
    setIdeaForm(emptyIdea);
    setIdeaSubmitted(false);
    setIdeaError("");
  }

  async function handleIdeaSubmit() {
    setIdeaSubmitting(true);
    setIdeaError("");
    const message = [
      `Name: ${ideaForm.name}`,
      `Email: ${ideaForm.email}`,
      `Sport / League: ${ideaForm.sport}`,
      `Data Needed: ${ideaForm.dataNeeded}`,
      `Current Users: ${ideaForm.currentUsers}`,
      `Years Running: ${ideaForm.yearsRunning}`,
      ``,
      `Description:`,
      ideaForm.description,
    ].join("\n");
    try {
      await submitFeedback({ type: "game_idea", game: "all", message });
      setIdeaSubmitted(true);
    } catch {
      setIdeaError("Something went wrong. Please try again.");
    } finally {
      setIdeaSubmitting(false);
    }
  }

  const [metrics, setMetrics] = useState({ participants: null, totalPot: null });
  const [records, setRecords] = useState([]);
  const [recordsGame, setRecordsGame] = useState("all");
  const [recordsLoading, setRecordsLoading] = useState(true);
  // Filter options come from the backend registry, so a newly registered
  // game appears here without editing this file.
  const [recordGames, setRecordGames] = useState([]);

  useEffect(() => {
    async function loadMetrics() {
      try {
        const [plPools, wcPools, mlbActiveSeason, psActiveSeason, nflSeason] =
          await Promise.all([
            PL_SEASON_ID ? getAllPools(PL_SEASON_ID).catch(() => []) : Promise.resolve([]),
            getAllWcPools(2).catch(() => []),
            getActiveSeason().catch(() => null),
            getPsActiveSeason().catch(() => null),
            getNflCurrentSeason().catch(() => null),
          ]);
        const mlbPools = mlbActiveSeason ? await getAllMlbPools(mlbActiveSeason.id).catch(() => []) : [];
        const psPools = psActiveSeason ? await getAllPsPools(psActiveSeason.id).catch(() => []) : [];
        const svPools = nflSeason ? await getAllSurvivorPools(nflSeason.id).catch(() => []) : [];
        const pkPools = nflSeason ? await getAllPickemPools(nflSeason.id).catch(() => []) : [];

        // Games don't agree on how they report this: most send archived_at,
        // World Cup sends an is_archived boolean. Checking only one of them
        // let archived pools count as active.
        const isArchived = (p) => !!(p.archived_at || p.is_archived);

        const active = [
          ...plPools,
          ...mlbPools,
          ...wcPools,
          ...psPools,
          ...svPools,
          ...pkPools,
        ].filter(
          (p) => !isArchived(p) && (!p.status || p.status === "active" || p.status === "drafting")
        );

        // Participants and pot describe what's in play now, so they come from
        // the same active set as the pool count rather than every pool loaded.
        const participants = active.reduce((sum, p) => sum + (p.user_count || 0), 0);
        const totalPot = active.reduce((sum, p) => sum + (p.total_pot || 0), 0);
        setMetrics({ activePools: active.length, participants, totalPot });
      } catch {
        // metrics are non-critical — leave as null
      }
    }
    loadMetrics();
  }, [PL_SEASON_ID]);

  useEffect(() => {
    setRecordsLoading(true);
    getRecords(recordsGame)
      .then(setRecords)
      .catch(() => setRecords([]))
      .finally(() => setRecordsLoading(false));
  }, [recordsGame]);

  useEffect(() => {
    getRecordGames().then(setRecordGames).catch(() => setRecordGames([]));
  }, []);

  const username = user?.username || "Player";

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleGameClick(game) {
    if (!game.active) return;
    navigate(game.route);
  }

  function handleGameDetails(game) {
    setSelectedGame(game);
    onOpen();
  }

  function handleOpenFromModal() {
    if (!selectedGame?.active) return;
    onClose();
    navigate(selectedGame.route);
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title={
          <>
            THE DEEP END
            <Box
              as="span"
              fontFamily="'Whisper', cursive"
              color={DEEP_END_RED}
              // Orbitron's 3px tracking looks wrong on a script face
              letterSpacing="normal"
              fontWeight="400"
              // em-relative so it scales with the header at both breakpoints
              fontSize="1.25em"
              ml={0}
            >
              games
            </Box>
          </>
        }
        mobileSingleRow
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="start" spacing={2} mb={10}>
          <Heading size="lg">Welcome {username}!</Heading>
          <Text fontSize="lg" color="whiteAlpha.900">
            Please select a game
          </Text>
        </VStack>

        {/* Relative wrapper anchors the edge fade, which must sit outside the
            scrolling box so it stays put instead of scrolling with the tiles. */}
        <Box position="relative">
        <Box
          overflowX="auto"
          bg={TOP_BAR_BLUE}
          borderRadius="xl"
          p={4}
          sx={{
            // Snapping makes a swipe land cleanly on the next card rather than
            // stopping halfway. scrollPaddingLeft matches the p={4} above so a
            // snapped tile aligns with the container's content edge, not its
            // border.
            scrollSnapType: "x mandatory",
            scrollPaddingLeft: "16px",
            "&::-webkit-scrollbar": {
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255,255,255,0.15)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.45)",
              borderRadius: "8px",
            },
          }}
        >
          <HStack spacing={6} align="stretch" minW="max-content">
            {games.map((game) => (
              <Box
                key={game.title}
                // 78vw rather than 85vw so a good slice of the next card shows.
                // At 85vw only ~19px peeked past the gap, which read as margin
                // rather than as more content.
                minW={{ base: "78vw", md: "320px" }}
                maxW={{ base: "78vw", md: "320px" }}
                sx={{ scrollSnapAlign: "start" }}
                bg="white"
                color="black"
                border={`3px solid ${TOP_BAR_BLUE}`}
                borderRadius="lg"
                boxShadow="lg"
                p={6}
                transition="0.2s ease"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "xl",
                }}
              >
                <VStack align="stretch" spacing={4} h="100%">
                  <Flex justify="center" align="center" h="80px" w="100%">
                    <img
                      src={game.logo}
                      alt={game.title}
                      style={{
                        maxHeight: "65px",
                        maxWidth: "140px",
                        objectFit: "contain",
                        display: "block",
                        ...game.logoStyle,
                      }}
                    />
                  </Flex>

                  <Heading size="md" textAlign="center">
                    {game.title}
                  </Heading>

                  <Text fontSize="sm" textAlign="center" minH="40px">
                    {game.subtitle}
                  </Text>

                  <HStack pt={2} spacing={3} justify="center" flexWrap="wrap">
                    <Button
                      bg={game.active ? TOP_BAR_BLUE : "gray.500"}
                      color="white"
                      size="sm"
                      onClick={() => handleGameClick(game)}
                      isDisabled={!game.active}
                      _hover={game.active ? { bg: "#00004a" } : {}}
                    >
                      {game.active ? "Open Game" : "Coming Soon"}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={TOP_BAR_BLUE}
                      color={TOP_BAR_BLUE}
                      onClick={() => handleGameDetails(game)}
                      _hover={{ bg: "gray.100" }}
                    >
                      Game Details
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </HStack>
        </Box>

          {/* Content continues under this fade. Sits above the tiles but takes
              no pointer events, so swiping through it still scrolls the row.
              Stops short of the bottom to clear the scrollbar.

              Mobile only: desktop keeps a visible styled scrollbar, which
              already signals the overflow. */}
          <Box
            display={{ base: "block", md: "none" }}
            position="absolute"
            top="4px"
            right="4px"
            bottom="18px"
            w="40px"
            bgGradient={`linear(to-r, transparent, ${TOP_BAR_BLUE})`}
            borderRightRadius="xl"
            pointerEvents="none"
            zIndex={1}
          />
        </Box>

        {/* ── Platform Metrics ── */}
        <Box
          mt={12}
          bg="white"
          borderRadius="xl"
          border={`2px solid ${TOP_BAR_BLUE}`}
          overflow="hidden"
          position="relative"
        >
          {/* Dashboard screenshot behind the numbers. Its own layer, same as
              the Excel pitch below, so the opacity doesn't fade the text too.
              Mobile gets a separate crop — the desktop shot is too wide to
              read anything from once it's squeezed into a phone-width box. */}
          <Box
            position="absolute"
            inset={0}
            bgImage={{
              base: `url(${process.env.PUBLIC_URL}/dashboard_mobile.png)`,
              md: `url(${process.env.PUBLIC_URL}/dashboard.png)`,
            }}
            bgSize="95%"
            bgPosition="center"
            bgRepeat="no-repeat"
            opacity={0.10}
            pointerEvents="none"
            zIndex={0}
          />

          {/* Header — same treatment as the records board below. */}
          <Box px={6} py={4} position="relative" zIndex={1}>
            <Heading
              fontFamily="'Orbitron', sans-serif"
              fontSize={{ base: "16px", md: "20px" }}
              letterSpacing="2px"
              fontWeight="700"
              color={TOP_BAR_BLUE}
              textAlign="center"
            >
              DEEP END METRICS
            </Heading>
          </Box>

          <Box p={8} position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={0}>

            {/* Games in Production — top-left on mobile */}
            <VStack spacing={1} align="center" py={4}
              borderRightWidth="1px" borderRightColor={TOP_BAR_BLUE}
              borderBottomWidth={{ base: "1px", md: "0" }} borderBottomColor={TOP_BAR_BLUE}>
              <Text fontFamily="'Orbitron', sans-serif" fontSize={{ base: "40px", md: "52px" }}
                fontWeight="700" lineHeight="1" color={TOP_BAR_BLUE}>
                {games.filter(g => g.active).length}
              </Text>
              <Text fontSize="xs" color={TOP_BAR_BLUE} textTransform="uppercase" letterSpacing="2px" textAlign="center">
                Games in Production
              </Text>
            </VStack>

            {/* Active Pools — top-right on mobile */}
            <VStack spacing={1} align="center" py={4}
              borderRightWidth={{ base: "0", md: "1px" }} borderRightColor={TOP_BAR_BLUE}
              borderBottomWidth={{ base: "1px", md: "0" }} borderBottomColor={TOP_BAR_BLUE}>
              <Text fontFamily="'Orbitron', sans-serif" fontSize={{ base: "40px", md: "52px" }}
                fontWeight="700" lineHeight="1" color={TOP_BAR_BLUE}>
                {metrics.activePools != null ? metrics.activePools : "—"}
              </Text>
              <Text fontSize="xs" color={TOP_BAR_BLUE} textTransform="uppercase" letterSpacing="2px" textAlign="center">
                Active Pools
              </Text>
            </VStack>

            {/* Active Participants — bottom-left on mobile */}
            <VStack spacing={1} align="center" py={4}
              borderRightWidth="1px" borderRightColor={TOP_BAR_BLUE}>
              <Text fontFamily="'Orbitron', sans-serif" fontSize={{ base: "40px", md: "52px" }}
                fontWeight="700" lineHeight="1" color={TOP_BAR_BLUE}>
                {metrics.participants != null ? metrics.participants : "—"}
              </Text>
              <Text fontSize="xs" color={TOP_BAR_BLUE} textTransform="uppercase" letterSpacing="2px" textAlign="center">
                Active
                <Box as="br" display={{ base: "block", md: "none" }} />
                {" "}Participants
              </Text>
            </VStack>

            {/* Total Prize Pool — bottom-right on mobile */}
            <VStack spacing={1} align="center" py={4}>
              <Text fontFamily="'Orbitron', sans-serif" fontSize={{ base: "40px", md: "52px" }}
                fontWeight="700" lineHeight="1" color={TOP_BAR_BLUE}>
                {metrics.totalPot != null ? (
                  <><Text as="span" fontSize={{ base: "26px", md: "34px" }}>$</Text>{metrics.totalPot}</>
                ) : "—"}
              </Text>
              <Text fontSize="xs" color={TOP_BAR_BLUE} textTransform="uppercase" letterSpacing="2px" textAlign="center">
                Active Prize Pool
              </Text>
            </VStack>

          </SimpleGrid>
          </Box>
        </Box>

        {/* ── Records Board ── */}
        {(() => {
          const GOLD = "#c9a227";
          const GAME_COLORS = {
            premier_league: "#00005f",
            mlb_hr_derby: "#c41e3a",
            world_cup:    "#2d6a4f",
          };
          return (
            <Box
              mt={6}
              border={`2px solid ${GOLD}`}
              borderRadius="xl"
              overflow="hidden"
            >
              {/* Header */}
              <Box bg={GOLD} px={6} py={4}>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  align="center"
                  justify="center"
                  gap={3}
                  position="relative"
                >
                  <HStack spacing={3}>
                    <FaTrophy color="white" size={20} />
                    <Heading
                      fontFamily="'Orbitron', sans-serif"
                      fontSize={{ base: "16px", md: "20px" }}
                      letterSpacing="2px"
                      fontWeight="700"
                      color="white"
                      textAlign="center"
                      whiteSpace={{ base: "normal", md: "nowrap" }}
                    >
                      DEEP END{" "}
                      <Box as="br" display={{ base: "block", md: "none" }} />
                      RECORDS BOARD
                    </Heading>
                    <FaTrophy color="white" size={20} />
                  </HStack>
                  <Box
                    position={{ base: "static", md: "absolute" }}
                    right={0}
                    top={{ md: "50%" }}
                    transform={{ md: "translateY(-50%)" }}
                  >
                  <Select
                    value={recordsGame}
                    onChange={(e) => setRecordsGame(e.target.value)}
                    bg="white"
                    color="black"
                    border="none"
                    fontWeight="semibold"
                    fontSize="sm"
                    w="auto"
                    minW="180px"
                    size="sm"
                    borderRadius="md"
                    cursor="pointer"
                  >
                    <option value="all">All Games</option>
                    {recordGames.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </Select>
                  </Box>
                </Flex>
              </Box>

              {/* Table */}
              <Box bg="white" overflowX="auto">
                {recordsLoading ? (
                  <Flex justify="center" py={10}>
                    <Spinner color={GOLD} size="lg" />
                  </Flex>
                ) : records.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py={10} fontSize="sm">
                    No records yet.
                  </Text>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead>
                      <tr style={{ background: "#f7f7f7", borderBottom: "2px solid #e2e8f0" }}>
                        <th style={{ padding: "10px 16px", textAlign: "center", color: "#555", fontWeight: 700, width: "40px" }}></th>
                        <th style={{ padding: "10px 16px", textAlign: "left",  color: "#555", fontWeight: 700 }}>Username</th>
                        <th style={{ padding: "10px 16px", textAlign: "left",  color: "#555", fontWeight: 700 }}>Pool</th>
                        <th style={{ padding: "10px 16px", textAlign: "left",  color: "#555", fontWeight: 700 }}>Category</th>
                        <th style={{ padding: "10px 16px", textAlign: "left",  color: "#555", fontWeight: 700 }}>Game</th>
                        <th style={{ padding: "10px 16px", textAlign: "left",  color: "#555", fontWeight: 700 }}>Season</th>
                        <th style={{ padding: "10px 16px", textAlign: "right", color: "#555", fontWeight: 700 }}>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "white" : "#fafafa", borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "10px 16px", textAlign: "center", fontSize: "16px" }}>🏆</td>
                          <td style={{ padding: "10px 16px", color: "#1a1a1a" }}>
                            <div style={{ fontWeight: 700 }}>{r.username}</div>
                            {r.team_name && (
                              <div style={{ fontSize: "12px", color: "#888", marginTop: "1px" }}>{r.team_name}</div>
                            )}
                          </td>
                          <td style={{ padding: "10px 16px", color: "#333" }}>{r.pool_name}</td>
                          <td style={{ padding: "10px 16px", color: "#555", fontSize: "13px" }}>{r.category}</td>
                          <td style={{ padding: "10px 16px" }}>
                            <span style={{
                              background: GAME_COLORS[r.game] || "#555",
                              color: "white",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}>
                              {r.game_label}
                            </span>
                          </td>
                          <td style={{ padding: "10px 16px", color: "#555" }}>{r.season_name}</td>
                          <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: GOLD }}>
                            {typeof r.score === "number" ? r.score.toLocaleString() : r.score}
                            <span style={{ fontSize: "11px", color: "#999", marginLeft: "4px" }}>{r.score_label}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Box>
            </Box>
          );
        })()}

        {/* ── Pitch Your Game ── */}
        <Box
          mt={6}
          bg="white"
          borderRadius="xl"
          border={`2px solid ${DIVIDER_GRAY}`}
          p={8}
          textAlign="center"
          position="relative"
          overflow="hidden"
        >
          {/* Spreadsheet screenshot behind the pitch. Sits in its own layer
              rather than on the Box so the opacity doesn't fade the text too. */}
          <Box
            position="absolute"
            inset={0}
            bgImage={`url(${process.env.PUBLIC_URL}/excel.png)`}
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            opacity={0.10}
            pointerEvents="none"
            zIndex={0}
          />

          <Box position="relative" zIndex={1}>
          <Heading size="lg" color={TOP_BAR_BLUE} mb={3}>
            Tired of running your pool in Excel? Pitch us your game and let us build it!
          </Heading>
          <Text fontSize="md" color={TOP_BAR_BLUE} mb={6}>
            <strong>Click below to give us the details.</strong>
          </Text>
          <Button
            bg="orange.400"
            color={TOP_BAR_BLUE}
            fontWeight="bold"
            px={8}
            _hover={{ bg: "gray.100" }}
            onClick={onIdeaOpen}
          >
            Submit Idea
          </Button>
          </Box>
        </Box>

        <Text
          mt={4}
          textAlign="center"
          fontSize="sm"
          color="white"
          fontWeight="bold"
          cursor="pointer"
          textDecoration="underline"
          _hover={{ color: "whiteAlpha.800" }}
          onClick={onContactOpen}
        >
          Contact Us
        </Text>

      </Container>

      {/* ── Mobile Feedback Button ── */}
      <Box
        as="button"
        display={{ base: "flex", md: "none" }}
        position="fixed"
        bottom={4}
        right={4}
        zIndex={1000}
        bg="orange.400"
        color="white"
        px={4}
        py={2}
        borderRadius="full"
        fontSize="sm"
        fontWeight="bold"
        boxShadow="0 2px 8px rgba(0,0,0,0.3)"
        onClick={onFeedbackOpen}
        _hover={{ bg: "orange.500" }}
      >
        Feedback
      </Box>

      {/* ── Feedback Tab ── */}
      <Box
        as="button"
        position="fixed"
        right="0"
        top="50%"
        zIndex={1000}
        display={{ base: "none", md: "block" }}
        bg="orange.400"
        color="white"
        borderRadius="0"
        px={3}
        py={5}
        cursor="pointer"
        onClick={onFeedbackOpen}
        boxShadow="-2px 2px 10px rgba(0,0,0,0.3)"
        _hover={{ bg: "orange.500" }}
        style={{ writingMode: "vertical-lr", transform: "translateY(-50%) rotate(180deg)" }}
      >
        <Text fontWeight="bold" letterSpacing="2px" fontSize="sm">FEEDBACK</Text>
      </Box>

      {/* ── Feedback Drawer ── */}
      <Drawer isOpen={isFeedbackOpen} placement="right" onClose={handleFeedbackClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            bg="#00005f"
            color="white"
            fontFamily="'Orbitron', sans-serif"
            letterSpacing="2px"
          >
            FEEDBACK
          </DrawerHeader>

          <DrawerBody pt={6}>
            {feedbackSubmitted ? (
              <VStack spacing={4} py={12} align="center">
                <Text fontSize="3xl">✓</Text>
                <Heading size="md" color="#00005f">Thanks for your feedback!</Heading>
                <Text color="gray.600" textAlign="center">
                  We&apos;ll review it and follow up if needed.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={5} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Type</FormLabel>
                  <Select
                    placeholder="Select type"
                    value={feedbackForm.type}
                    onChange={(e) => setFeedbackForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <option value="report_issue">Report Issue</option>
                    <option value="provide_feedback">Provide Feedback</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Game</FormLabel>
                  <Select
                    placeholder="Select game"
                    value={feedbackForm.game}
                    onChange={(e) => setFeedbackForm((f) => ({ ...f, game: e.target.value }))}
                  >
                    <option value="all">All Games</option>
                    {games.map((g) => (
                      <option key={g.title} value={g.title}>{g.title}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Details</FormLabel>
                  <Textarea
                    placeholder="Please provide as many details as possible..."
                    value={feedbackForm.message}
                    onChange={(e) => setFeedbackForm((f) => ({ ...f, message: e.target.value }))}
                    rows={8}
                    resize="vertical"
                  />
                </FormControl>

                {feedbackError && (
                  <Text color="red.500" fontSize="sm">{feedbackError}</Text>
                )}
              </VStack>
            )}
          </DrawerBody>

          <DrawerFooter borderTop="1px solid" borderColor="gray.200">
            {feedbackSubmitted ? (
              <Button onClick={handleFeedbackClose} colorScheme="gray">Close</Button>
            ) : (
              <HStack spacing={3} w="100%" justify="flex-end">
                <Button variant="outline" onClick={handleFeedbackClose}>Cancel</Button>
                <Button
                  bg="#00005f"
                  color="white"
                  _hover={{ bg: "#00004a" }}
                  isLoading={feedbackSubmitting}
                  isDisabled={!feedbackForm.type || !feedbackForm.game || !feedbackForm.message.trim()}
                  onClick={handleFeedbackSubmit}
                >
                  Submit
                </Button>
              </HStack>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ── Contact Us Modal ── */}
      <Modal isOpen={isContactOpen} onClose={handleContactClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
          <ModalHeader color={TOP_BAR_BLUE}>Contact Us</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />
          <ModalBody pb={2}>
            {contactSubmitted ? (
              <VStack spacing={4} py={8} align="center">
                <Text fontSize="2xl">✓</Text>
                <Heading size="md" color={TOP_BAR_BLUE}>Message sent!</Heading>
                <Text color="gray.600" textAlign="center">We&apos;ll get back to you soon.</Text>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <Textarea
                  placeholder="How can we help? Please provide as many details as possible."
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  rows={6}
                  resize="vertical"
                />
                {contactError && <Text color="red.500" fontSize="sm">{contactError}</Text>}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={handleContactClose}>
                {contactSubmitted ? "Close" : "Cancel"}
              </Button>
              {!contactSubmitted && (
                <Button
                  bg={TOP_BAR_BLUE}
                  color="white"
                  _hover={{ bg: "#00004a" }}
                  isLoading={contactSubmitting}
                  isDisabled={!contactMessage.trim()}
                  onClick={handleContactSubmit}
                >
                  Submit
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Submit Idea Modal ── */}
      <Modal isOpen={isIdeaOpen} onClose={handleIdeaClose} isCentered size="xl" motionPreset="scale" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
          <ModalHeader color={TOP_BAR_BLUE}>Submit a Game Idea</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />
          <ModalBody pb={6}>
            {ideaSubmitted ? (
              <VStack spacing={4} py={8} align="center">
                <Text fontSize="2xl">🎉</Text>
                <Heading size="md" color={TOP_BAR_BLUE}>Thanks for your submission!</Heading>
                <Text color="gray.600" textAlign="center">
                  We&apos;ll review your idea and be in touch.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Name</FormLabel>
                    <Input
                      placeholder="Your name"
                      value={ideaForm.name}
                      onChange={(e) => handleIdeaChange("name", e.target.value)}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={ideaForm.email}
                      onChange={(e) => handleIdeaChange("email", e.target.value)}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Sport/League</FormLabel>
                  <Input
                    placeholder="e.g. NFL, NBA, Soccer..."
                    value={ideaForm.sport}
                    onChange={(e) => handleIdeaChange("sport", e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Data Needed</FormLabel>
                  <Input
                    placeholder="e.g. weekly scores, player stats, standings..."
                    value={ideaForm.dataNeeded}
                    onChange={(e) => handleIdeaChange("dataNeeded", e.target.value)}
                  />
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Current Number of Users</FormLabel>
                    <NumberInput min={1} value={ideaForm.currentUsers} onChange={(v) => handleIdeaChange("currentUsers", v)}>
                      <NumberInputField placeholder="e.g. 12" />
                    </NumberInput>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Years Running</FormLabel>
                    <NumberInput min={0} value={ideaForm.yearsRunning} onChange={(v) => handleIdeaChange("yearsRunning", v)}>
                      <NumberInputField placeholder="e.g. 3" />
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">General Description</FormLabel>
                  <Textarea
                    placeholder="Describe how your pool works — scoring, rules, format, etc."
                    value={ideaForm.description}
                    onChange={(e) => handleIdeaChange("description", e.target.value)}
                    rows={4}
                  />
                </FormControl>
                {ideaError && <Text color="red.500" fontSize="sm">{ideaError}</Text>}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={handleIdeaClose}>
                {ideaSubmitted ? "Close" : "Cancel"}
              </Button>
              {!ideaSubmitted && (
                <Button
                  bg={TOP_BAR_BLUE}
                  color="white"
                  _hover={{ bg: "#00004a" }}
                  isLoading={ideaSubmitting}
                  onClick={handleIdeaSubmit}
                  isDisabled={!ideaForm.name || !ideaForm.email || !ideaForm.sport || !ideaForm.description}
                >
                  Submit
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="lg"
        motionPreset="scale"
      >
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent
          bg="white"
          color="black"
          border={`3px solid ${TOP_BAR_BLUE}`}
          borderRadius="lg"
        >
          <ModalHeader color={TOP_BAR_BLUE} pr={12}>
            {selectedGame?.details?.heading || "Game Details"}
          </ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />

          <ModalBody pb={4}>
            {selectedGame && (
              <VStack align="stretch" spacing={5}>
                <Flex justify="center" align="center" w="100%" h="90px">
                  <img
                    src={selectedGame.logo}
                    alt={selectedGame.title}
                    style={{
                      maxHeight: "70px",
                      maxWidth: "180px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Flex>

                <Text>{selectedGame.details?.intro}</Text>

                <VStack align="start" spacing={2}>
                  {selectedGame.details?.bullets?.map((item, index) => {
                    const isObj = typeof item === "object";
                    const label = isObj ? item.text : item;
                    return (
                      <Text key={index} pl={isObj && item.indent ? 6 : 0}>
                        {isObj && item.indent ? "◦" : "•"} {label}
                      </Text>
                    );
                  })}
                </VStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              {selectedGame?.active ? (
                <Button
                  bg={TOP_BAR_BLUE}
                  color="white"
                  onClick={handleOpenFromModal}
                  _hover={{ bg: "#00004a" }}
                >
                  Open Game
                </Button>
              ) : (
                <Button isDisabled colorScheme="gray">
                  Coming Soon
                </Button>
              )}

              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}